import _ from 'lodash';
import { ApolloError, useMutation, useQuery } from '@apollo/client';

import { createCacheModifier } from '../cache/basic.cache';
import {
  CREATE_AGE_GROUP,
  AGE_GROUP_BUCKET_CHOICE,
  AGE_GROUP_FIELDS,
  GET_AGE_GROUPS,
  GET_AGE_GROUPS_IN_ASSESSMENT,
  GET_AGE_GROUP_BUCKET_SETTINGS,
  GET_AGE_GROUP_JERSEY_RETENTION,
  SET_AGE_GROUP_BUCKET,
  SET_AGE_GROUP_JERSEY_RETENTION,
  UPDATE_AGE_GROUP,
} from '../queries/age-group.queries';
import { useLogQueryError } from '../hooks/use-log-query-error';
import { keepWellFormedIds, selectAgeGroupIdsInAssessment } from '../helpers/age-group.helpers';
import { getAuthUser, setAuthUserSettings } from './auth.service';
import { getAllowedMemberRoles, getCurrUser } from './user.service';
import { getCurrMember } from './member.service';
import { MEMBER_FIELDS } from '../queries/member.queries';
import { EXCLUSIVE_ROLES, MemberListType, ROLES } from '../../../_configuration';
import { useMemo } from 'react';

// Stable empty default so consumers' memos keyed on the list don't re-run every render.
const EMPTY_BUCKET_SETTINGS: IAgeGroupBucketSettings = { options: [], ageGroups: [] };

// Global-admin read: one assessment's age groups, newest first, with the bucket the server would
// match for each. Skips until an assessment is known — the server refuses a missing assessmentId.
export const getAgeGroupBucketSettings = (assessmentId?: string | null, options?: { skip?: boolean }) => {
  const skipped = Boolean(options?.skip) || !assessmentId;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(GET_AGE_GROUP_BUCKET_SETTINGS, {
    skip: skipped,
    variables: { assessmentId },
    fetchPolicy: 'cache-and-network',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLogQueryError(error, 'getAgeGroupBucketSettings');

  return {
    bucketSettings: (data?.ageGroupBucketSettings ?? EMPTY_BUCKET_SETTINGS) as IAgeGroupBucketSettings,
    // A skipped query has no answer yet — reporting `false` reads to a consumer as
    // "loaded, and the assessment has no age groups".
    loading: loading || skipped,
    error,
  };
};

export const buildSetAgeGroupBucket = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(SET_AGE_GROUP_BUCKET);

  return {
    ...rest,
    // The optimistic update writes the two fields the Select reads at once (rolled back on failure);
    // normalization can't reach the AgeGroupBucketSetting row, so the refetch brings the server-derived rest.
    setAgeGroupBucket: (_id: string, value: string | null) => mutation({
      variables: { _id, value },
      optimisticResponse: {
        setAgeGroupBucket: { __typename: 'AgeGroup', _id, bucket: value, isBucketManual: true },
      },
      refetchQueries: ['GetAgeGroupBucketSettings'],
      update: cache => {
        cache.writeFragment({
          id: cache.identify({ __typename: 'AgeGroupBucketSetting', _id }),
          fragment: AGE_GROUP_BUCKET_CHOICE,
          data: { __typename: 'AgeGroupBucketSetting', bucket: value, isBucketManual: true },
        });
      },
    }),
  };
};

export const getActiveAgeGroupIds = () => {
  const { user } = getCurrUser();

  return _.get(user, 'settings.activeAgeGroupIds');
};

const NO_AGE_GROUP_IDS: string[] = [];

// The active selection narrowed to the active assessment, for every request that sends both.
// `ageGroupIds` is undefined until known, so a caller never sends the unfiltered or a false-empty list.
export const useActiveAgeGroupIdsInAssessment = ({ skip = false }: { skip?: boolean } = {}): {
  ageGroupIds?: string[];
  loading: boolean;
  error?: ApolloError;
} => {
  const user = getAuthUser();
  const assessmentId: string | undefined = user?.settings.activeAssessmentId;
  const savedIds: string[] = user?.settings.activeAgeGroupIds || NO_AGE_GROUP_IDS;
  // One malformed saved id would fail the whole lookup (a server CastError) and stall every caller.
  const selectedIds = useMemo(() => keepWellFormedIds(savedIds), [savedIds]);
  const nothingSelected = !assessmentId || !selectedIds.length;

  const { data, error } = useQuery(GET_AGE_GROUPS_IN_ASSESSMENT, {
    skip: skip || !user || nothingSelected,
    variables: { assessmentId, ageGroupIds: selectedIds },
    fetchPolicy: 'cache-and-network'
  });

  useLogQueryError(error, 'useActiveAgeGroupIdsInAssessment');

  const ageGroupIds = useMemo(() => {
    if (nothingSelected) return NO_AGE_GROUP_IDS;
    if (!data?.ageGroups || !assessmentId) return undefined;

    return selectAgeGroupIdsInAssessment(selectedIds, _.compact(data.ageGroups), assessmentId);
  }, [nothingSelected, data, selectedIds, assessmentId]);

  return { ageGroupIds, loading: !skip && !ageGroupIds && !error, error };
};

const NO_AGE_GROUPS: IAgeGroup[] = [];
const NO_ALLOWED_AGE_GROUPS: IAgeGroupWithAllowedRoles[] = [];

// TODO: We should probably cache this
export const useRoleAllowedAgeGroups = ({ageGroups = NO_AGE_GROUPS, requiredRoles = []}: {ageGroups?: IAgeGroup[]; requiredRoles?: string[]} ): IAgeGroupWithAllowedRoles[] => {
  const { member } = getCurrMember()
  // Keyed by value (callers pass inline arrays); JSON keeps [undefined] and [''] distinct from [].
  const requiredRolesKey = JSON.stringify(requiredRoles);
  const { roles, checkRoles } = useMemo(() => {
    const named = _.compact(requiredRoles);
    if (named.length < requiredRoles.length) console.warn('useRoleAllowedAgeGroups: blank required role ignored', requiredRoles);
    return { roles: named, checkRoles: requiredRoles.length > 0 };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredRolesKey]);

  const availableAgeGroupsForRoles = useMemo(() => {
    if (!member || !ageGroups.length) return NO_ALLOWED_AGE_GROUPS;
    if (!checkRoles) return ageGroups as IAgeGroupWithAllowedRoles[];
    if (!roles.length) return NO_ALLOWED_AGE_GROUPS;

    let remainingRoles = [...roles];
    const allowedAgeGroupLookup: { [key: string]: string[] } = {};
    for (const ageGroup of ageGroups) {
      if (!remainingRoles.length) break;

      const allowedRoles = getAllowedMemberRoles({member, roles: remainingRoles, ageGroupId: ageGroup._id, assessmentId: ageGroup.assessmentId})
      for (const role of allowedRoles) {
        if (!role.ageGroupId && !EXCLUSIVE_ROLES[MemberListType.AGE_GROUP].includes(role.role)) {
          // If the role is not an age group specific role, then okay it for all age groups and remove it from the remaining roles to check
          remainingRoles = remainingRoles.filter(r => r !== role.role)
          ageGroups.forEach((a) => {
            allowedAgeGroupLookup[a._id] = [...(allowedAgeGroupLookup[a._id] ?? []), role.role]
          })
        } else {
          allowedAgeGroupLookup[ageGroup._id] = [...(allowedAgeGroupLookup[ageGroup._id] ?? []), role.role]
        }
      }
    }

    const allowed = Object.entries(allowedAgeGroupLookup).map(([ageGroupId, allowedRoles]) => ({...ageGroups.find(a => a._id === ageGroupId), allowedRoles} as IAgeGroupWithAllowedRoles));
    return allowed.length ? allowed : NO_ALLOWED_AGE_GROUPS;
  }, [ageGroups, member, roles, checkRoles])

  return availableAgeGroupsForRoles
};

export const getAgeGroups = ({requiredRoles = [], useActive}: {requiredRoles?: string[]; useActive?: boolean } = {}): {ageGroups: IAgeGroupWithAllowedRoles[]; loading: boolean; error?: ApolloError} => {
  const user = getAuthUser();

  const assessmentId = user?.settings.activeAssessmentId;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, previousData, loading, error } = useQuery(GET_AGE_GROUPS, {
    skip: !user,
    variables: { assessmentId, ageGroupIds: useActive ? user?.settings.activeAgeGroupIds : undefined },
    fetchPolicy: 'cache-and-network'
  });

  // An unseen variable set reads back undefined (creating an age group re-keys this query), so hold
  // the last list — but previousData survives an assessment switch, so keep only the active one's.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const ageGroups = useMemo(() => (
    data?.ageGroups
    || (previousData?.ageGroups || []).filter((a: IAgeGroup) => a.assessmentId === assessmentId)
  ), [data, previousData, assessmentId]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const allowedAgeGroups = useRoleAllowedAgeGroups({ageGroups, requiredRoles})

  return {
    ageGroups: allowedAgeGroups,
    loading,
    error
  };
};

export const buildCreateAgeGroup = () => {
  const user = getAuthUser();
  const { member } = getCurrMember();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation(CREATE_AGE_GROUP);

  return {
    createAgeGroup: (name: string) => mutation({
      variables: { data: { name, assessmentId: user?.settings?.activeAssessmentId } },
      update: (cache, {data}) => {
        if (data.createAgeGroup._id) {
          cache.modify({
            fields: {
              members: createCacheModifier({
                cache,
                createdDoc: {
                  ...member,
                  rolesByAgeGroup: [...member.rolesByAgeGroup, {ageGroupId: data.createAgeGroup._id, roles: Object.values(ROLES)}]
                },
                fragment: MEMBER_FIELDS,
                fragmentName: 'MemberFields',
                modelName: 'Member'
              })
            }
          });

          setAuthUserSettings((settings) => ({
            // Deduped: createAgeGroup upserts, so re-importing an existing name returns an
            // id already held here, and a duplicate makes the filter prune settings mid-import.
            activeAgeGroupIds: _.uniq([...(settings.activeAgeGroupIds || []), data.createAgeGroup._id]),
          }));
        }
      },
      optimisticResponse: {
        __typename: 'Mutation',
        createAgeGroup: {
          __typename: 'AgeGroup',
          name,
          companyId: user?.settings?.activeCompanyId
        }
      }
    })
  }
}

export const buildUpdateAgeGroup = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation(UPDATE_AGE_GROUP);

  return {
    updateAgeGroup: (ageGroup: IAgeGroup, stages: IAssessmentStage[]) => {
      const nextCurrentStageNum = ageGroup.currentStageNum + 1
      return mutation({
        variables: { _id: ageGroup._id, data: { currentStage: nextCurrentStageNum }},
        update: (cache, {data}) => {
          if (data) {
            cache.modify({
              fields: {
                ageGroups: createCacheModifier({
                  cache,
                  createdDoc: data.updateAgeGroup,
                  fragment: AGE_GROUP_FIELDS,
                  fragmentName: 'AgeGroupFields',
                  modelName: 'AgeGroup'
                })
              }
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateAgeGroup: {
            ...ageGroup,
            __typename: 'AgeGroup',
            currentStageNum: nextCurrentStageNum,
            currentStage: stages.length > nextCurrentStageNum ? stages.find(s => s.order === nextCurrentStageNum) ?? null : null,
            nextStage: stages.length > nextCurrentStageNum + 1 ? stages.find(s => s.order === nextCurrentStageNum + 1) ?? null : null,
          }
        }
      })
    }
  }
}

export type AgeGroupJerseyRetentionRow = Pick<IAgeGroup, '_id' | 'name' | 'retainJerseyAssignments'>;

const NO_JERSEY_RETENTION_ROWS: AgeGroupJerseyRetentionRow[] = [];

export const getAgeGroupJerseyRetention = (assessmentId?: string | null) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(GET_AGE_GROUP_JERSEY_RETENTION, {
    skip: !assessmentId,
    variables: { assessmentId },
    fetchPolicy: 'cache-and-network',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLogQueryError(error, 'getAgeGroupJerseyRetention');

  return { ageGroups: (data?.ageGroups ?? NO_JERSEY_RETENTION_ROWS) as AgeGroupJerseyRetentionRow[], loading, error };
};

export const buildSetAgeGroupJerseyRetention = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation] = useMutation(SET_AGE_GROUP_JERSEY_RETENTION);

  return {
    // null clears the age group's choice. Check-in's per-ice-time setting is resolved on the server, so it refetches.
    setAgeGroupJerseyRetention: (_id: string, value: boolean | null) => mutation({
      variables: { _id, data: { retainJerseyAssignments: value } },
      refetchQueries: ['GetCheckIn'],
    }),
  };
};
